import { KpiObjectType } from '@/api/entities/kpi';
import { HelpTip } from './types';

const SCOPE_FR =
  'Administrateurs et modérateurs voient toute la plateforme ; les autres utilisateurs ne voient que leur périmètre (groupes, sociétés, exploitations auxquels ils sont rattachés).';
const SCOPE_EN =
  'Admins and moderators see the whole platform; other users only see their scope (groups, companies, branches they are linked to).';

export const DASHBOARD_TIPS = {
  /* === Home dashboard === */
  groupsCount: {
    title: { fr: 'Groupes', en: 'Groups' },
    text: {
      fr: `Nombre de groupes actifs (les groupes désactivés ne sont pas comptés). ${SCOPE_FR}`,
      en: `Number of active groups (deactivated groups are not counted). ${SCOPE_EN}`,
    },
    docSlug: 'tableau-de-bord',
  },
  companiesCount: {
    title: { fr: 'Sociétés', en: 'Companies' },
    text: {
      fr: `Nombre de sociétés actives (les sociétés désactivées ne sont pas comptées). ${SCOPE_FR}`,
      en: `Number of active companies (deactivated companies are not counted). ${SCOPE_EN}`,
    },
    docSlug: 'tableau-de-bord',
  },
  branchesCount: {
    title: { fr: 'Exploitations', en: 'Branches' },
    text: {
      fr: `Nombre d'exploitations actives (les exploitations désactivées ne sont pas comptées). ${SCOPE_FR}`,
      en: `Number of active branches (deactivated branches are not counted). ${SCOPE_EN}`,
    },
    docSlug: 'tableau-de-bord',
  },
  inspectionsCount: {
    title: { fr: 'Inspections', en: 'Inspections' },
    text: {
      fr: `Nombre total d'inspections terminées, depuis le début (toutes dates confondues), y compris celles dont la vidéo ou le rapport sont encore en cours de traitement. Les inspections non commencées ou en cours ne sont pas comptées. ${SCOPE_FR}`,
      en: `Total number of completed inspections, since the beginning (all dates), including those whose video or report is still being processed. Inspections not started or in progress are not counted. ${SCOPE_EN}`,
    },
    docSlug: 'tableau-de-bord',
  },
  lastRegisteredUsers: {
    title: {
      fr: 'Derniers utilisateurs inscrits',
      en: 'Last registered users',
    },
    text: {
      fr: `Les 2 derniers comptes utilisateurs créés, du plus récent au plus ancien. ${SCOPE_FR}`,
      en: `The 2 most recently created user accounts, newest first. ${SCOPE_EN}`,
    },
    docSlug: 'tableau-de-bord',
  },
  activities: {
    title: { fr: 'Activités', en: 'Activities' },
    text: {
      fr: `Inspections qui attendent une action de votre part, à l'instant présent (aucune période). ${SCOPE_FR}`,
      en: `Inspections waiting for an action on your side, right now (no period). ${SCOPE_EN}`,
    },
    docSlug: 'tableau-de-bord',
  },
  toModerate: {
    title: { fr: 'À vérifier', en: 'To check' },
    text: {
      fr: "Nombre d'inspections dont le rapport est disponible et qui n'ont pas encore été modérées (statut de modération « À modérer »).",
      en: 'Number of inspections whose report is available and which have not been moderated yet (moderation status "To moderate").',
    },
    docSlug: 'statuts-d-inspection',
  },
  toValidate: {
    title: { fr: 'À valider', en: 'To validate' },
    text: {
      fr: "Nombre d'inspections déjà modérées, avec un rapport disponible, dont au moins un résultat n'a pas encore été validé. Les inspections des groupes pour lesquels la modération est obligatoire ne sont pas comptées ici.",
      en: 'Number of inspections already moderated, with an available report, for which at least one result has not been validated yet. Inspections of groups where moderation is mandatory are not counted here.',
    },
    docSlug: 'statuts-d-inspection',
  },
  monthlyChart: {
    title: { fr: 'Évolution mensuelle', en: 'Monthly trend' },
    text: {
      fr: `Pour chaque mois de l'année civile en cours (de janvier au mois actuel) : nombre d'inspections terminées créées dans le mois (violet) et nombre de comptes utilisateurs créés dans le mois (bleu). Le mois en cours est partiel. ${SCOPE_FR}`,
      en: `For each month of the current calendar year (January to the current month): number of completed inspections created in the month (purple) and number of user accounts created in the month (blue). The current month is partial. ${SCOPE_EN}`,
    },
    docSlug: 'tableau-de-bord',
  },
  endingSubscriptions: {
    title: {
      fr: "Groupes en fin d'abonnement",
      en: 'Groups near end of subscription',
    },
    text: {
      fr: "Groupes actifs dont l'abonnement a une date de fin qui n'est pas encore passée (6 au maximum). Le bloc est masqué lorsqu'aucun groupe n'est concerné.",
      en: 'Active groups whose subscription has an end date that has not passed yet (6 at most). The block is hidden when no group is concerned.',
    },
    docSlug: 'tableau-de-bord',
  },

  /* === AI KPI (equipments / damages) === */
  kpiEvaluatedImages: {
    title: { fr: 'Images évaluées', en: 'Evaluated images' },
    text: {
      fr: "Nombre d'images du jeu de test sur lesquelles le modèle a été évalué lors de la dernière évaluation publiée (date affichée en haut à droite). En dessous : nombre de classes réellement mesurées, c'est-à-dire ayant au moins une annotation.",
      en: 'Number of test-set images the model was evaluated on in the last published evaluation (date displayed top right). Below: number of classes actually measured, i.e. with at least one annotation.',
    },
    docSlug: 'kpi-equipements',
  },
  kpiMIou: {
    title: { fr: 'mIoU (IoU moyen)', en: 'mIoU (mean IoU)' },
    text: {
      fr: "L'IoU (Intersection over Union) mesure le recouvrement entre la zone détectée par l'IA et la zone annotée à la main : 1 = recouvrement parfait, 0 = aucun. Le mIoU est la moyenne simple des IoU de toutes les classes ayant au moins une annotation (chaque classe pèse autant, quel que soit son nombre d'annotations). Couleur : vert ≥ 0,75, orange ≥ 0,5, rouge en dessous.",
      en: 'IoU (Intersection over Union) measures the overlap between the area detected by the AI and the hand-annotated area: 1 = perfect overlap, 0 = none. mIoU is the simple average of the IoU of every class with at least one annotation (each class weighs the same, whatever its number of annotations). Colour: green ≥ 0.75, orange ≥ 0.5, red below.',
    },
    docSlug: 'kpi-equipements',
  },
  kpiTiers: {
    title: {
      fr: 'Répartition par niveau de score',
      en: 'Breakdown by score level',
    },
    text: {
      fr: "Nombre de classes (ayant au moins une annotation) par niveau d'IoU : score élevé ≥ 0,75, score moyen entre 0,5 et 0,75, score faible < 0,5.",
      en: 'Number of classes (with at least one annotation) per IoU level: high score ≥ 0.75, medium score between 0.5 and 0.75, low score < 0.5.',
    },
    docSlug: 'kpi-equipements',
  },
  kpiIouDistribution: {
    title: { fr: 'Distribution IoU', en: 'IoU distribution' },
    text: {
      fr: "Histogramme du nombre de classes dans chaque tranche d'IoU de 0,1 (0–0,1, 0,1–0,2… 0,9–1). La couleur d'une barre dépend de la borne basse de la tranche : vert à partir de 0,7, orange de 0,5 à 0,7, rouge en dessous. Les classes sans annotation sont exclues.",
      en: 'Histogram of the number of classes in each 0.1 IoU bucket (0–0.1, 0.1–0.2… 0.9–1). The colour of a bar depends on the lower bound of the bucket: green from 0.7, orange from 0.5 to 0.7, red below. Classes without annotations are excluded.',
    },
    docSlug: 'kpi-equipements',
  },
  kpiTopFive: {
    title: { fr: 'Top 5', en: 'Top 5' },
    text: {
      fr: "Les 5 classes ayant le meilleur IoU, avec leur nombre d'annotations. Seules les classes ayant au moins une annotation sont classées.",
      en: 'The 5 classes with the best IoU, with their number of annotations. Only classes with at least one annotation are ranked.',
    },
    docSlug: 'kpi-equipements',
  },
  kpiFlopFive: {
    title: { fr: 'Flop 5', en: 'Flop 5' },
    text: {
      fr: "Les 5 classes ayant le plus faible IoU : ce sont les priorités d'amélioration du modèle. Un faible nombre d'annotations explique souvent un mauvais score.",
      en: 'The 5 classes with the lowest IoU: they are the priorities to improve the model. A low number of annotations often explains a poor score.',
    },
    docSlug: 'kpi-equipements',
  },
  kpiPerClassIou: {
    title: { fr: 'IoU par classe', en: 'IoU per class' },
    text: {
      fr: "IoU de chaque classe ayant au moins une annotation, de la plus faible à la plus élevée. Couleur : vert ≥ 0,75, orange ≥ 0,5, rouge en dessous. Survolez une barre pour voir le score exact et le nombre d'annotations.",
      en: 'IoU of every class with at least one annotation, from lowest to highest. Colour: green ≥ 0.75, orange ≥ 0.5, red below. Hover a bar to see the exact score and the number of annotations.',
    },
    docSlug: 'kpi-equipements',
  },
  kpiPerClassAnnotations: {
    title: { fr: 'Annotations par classe', en: 'Annotations per class' },
    text: {
      fr: "Nombre d'annotations (zones dessinées à la main) de chaque classe dans le jeu d'évaluation, de la plus rare à la plus fréquente. Les barres orange signalent les classes rares : moins de 10 % des annotations de la classe la plus fréquente.",
      en: 'Number of annotations (hand-drawn areas) of every class in the evaluation set, from rarest to most frequent. Orange bars flag rare classes: less than 10% of the annotations of the most frequent class.',
    },
    docSlug: 'kpi-equipements',
  },
} satisfies Record<string, HelpTip>;

/** KPI tips are shared by both KPI pages: points "Learn more" to the right page. */
export const getKpiTip = (tip: HelpTip, type: KpiObjectType): HelpTip => ({
  ...tip,
  docSlug: type === KpiObjectType.DAMAGE ? 'kpi-dommages' : 'kpi-equipements',
});
